import store from '@/store';
import { dasherize } from 'utils/Helpers';

/** Default exchange entry when we cannot build a schedule / ticket-list URL. */
export const EXCHANGE_HOME_PATH = '/exchange';

/**
 * Appends the `?transporter=` query param every `/exchange/schedule|ticket-list/...` builder
 * carries (task 8.5): `ExchangeSecondStep` reads the chosen line only from `location.search`,
 * so a path built without it loses the line on a reload or a redirect.
 * @param {string} path - Path already built, without a query string
 * @param {string} [transporter] - Transporter abbr chosen via the line selector
 * @returns {string}
 */
function withTransporterParam(path, transporter) {
  return transporter ? `${path}?transporter=${encodeURIComponent(transporter)}` : path;
}

/**
 * Builds `/exchange/schedule|ticket-list/...` paths from segments (reads ticket-list flag from
 * store). `transporter` (task 8.5 of openspec/changes/iamsa-b2c-ticket-exchange, platform-sre) is
 * carried as a `?transporter=` query param, not a path segment — the existing positional
 * segments (op/NIT/origin/destination/email/document) already collide in length with unrelated
 * route patterns (see `App.js`'s `/exchange/schedule/:operationNumbers/:origin/:destination` vs
 * `/exchange/schedule/:operationNumbers/:NIT`); a query param is additive and can't collide with
 * either.
 */
export function createSchedulePath(
  operationNumbers,
  NIT,
  origin,
  destination,
  email,
  document,
  transporter,
) {
  const { whitelabelConfig } = store.getState();

  let url = `/exchange/${
    whitelabelConfig.features.USE_EXCHANGE_TICKET_LIST_ROUTE ? 'ticket-list' : 'schedule'
  }`;
  if (operationNumbers) url += `/${operationNumbers}`;
  if (NIT) url += `/${NIT}`;
  if (origin) url += `/${origin}`;
  if (destination) url += `/${destination}`;
  if (email) url += `/${email}`;
  if (document) url += `/${document}`;
  return withTransporterParam(url, transporter);
}

/**
 * Build the exchange schedule URL path with proper origin/destination from the selected trip.
 * @param {string} operationNumbers - The operation numbers for the exchange
 * @param {object} trip - The selected trip object (depart or return)
 * @param {string} fallbackOrigin - Fallback origin from URL params
 * @param {string} fallbackDestination - Fallback destination from URL params
 * @param {string} [transporter] - Transporter abbr chosen via the line selector (task 8.5)
 * @returns {string} The formatted URL path for the exchange schedule
 */
export function buildExchangeSchedulePath(
  operationNumbers,
  trip,
  fallbackOrigin,
  fallbackDestination,
  transporter,
) {
  const tripOrigin = trip?.originDisplay ? dasherize(trip.originDisplay) : fallbackOrigin;
  const tripDestination = trip?.destinationDisplay
    ? dasherize(trip.destinationDisplay)
    : fallbackDestination;

  return withTransporterParam(
    `/exchange/schedule/${operationNumbers}/${tripOrigin}/${tripDestination}`,
    transporter,
  );
}

/**
 * Single path segment for /exchange/schedule/:op/:origin/:destination (e.g. lima, ica).
 * @param {string} [slug] - API slug (e.g. origin_slug)
 * @param {string} [display] - Human label; dasherized if slug missing
 * @param {string|number} [fallbackId] - Often cityId from purchase fragments
 * @returns {string}
 */
function exchangeSchedulePlaceSegment(slug, display, fallbackId) {
  if (slug) return String(slug).toLowerCase();
  if (display) return dasherize(display);
  if (fallbackId != null && `${fallbackId}`.trim() !== '') return String(fallbackId).toLowerCase();
  return '';
}

/**
 * Redirect path after an exchange seat-lock timeout / window error, using Redux `exchange` + `purchase`.
 * Always uses **schedule** (`/exchange/schedule/:op/:origin/:destination`), e.g. `/exchange/schedule/EE8FAA/lima/ica`,
 * never the `ticket-list` path — even when `USE_EXCHANGE_TICKET_LIST_ROUTE` is on (that route is for other flows).
 * Falls back to {@link EXCHANGE_HOME_PATH} if operation or place segments are missing.
 * @returns {string}
 */
export function getExchangeLockScheduleRedirectPath() {
  const { exchange } = store.getState();

  const fromOperationNumbers =
    exchange.operationNumbers?.length > 0 ? exchange.operationNumbers : null;
  const fromPassengers = (exchange.passengers || []).map((p) => p.transporterKey).filter(Boolean);
  const nums =
    fromOperationNumbers ||
    (fromPassengers.length > 0 ? fromPassengers : null) ||
    (exchange.ticket?.operationNumber ? [exchange.ticket.operationNumber] : []);

  const routeOp = exchange.routeOperationSegment?.trim();
  const opSegment = routeOp || (nums.length ? nums.join(',') : '');
  if (!opSegment) return EXCHANGE_HOME_PATH;

  const { trip, ticket } = exchange;

  const origin = ticket?.origin
    ? dasherize(ticket.origin)
    : exchangeSchedulePlaceSegment(trip?.originSlug, trip?.originDisplay, null);
  const destination = ticket?.destination
    ? dasherize(ticket.destination)
    : exchangeSchedulePlaceSegment(trip?.destinationSlug, trip?.destinationDisplay, null);

  if (!origin || !destination) return EXCHANGE_HOME_PATH;

  return withTransporterParam(
    `/exchange/schedule/${opSegment}/${origin}/${destination}`,
    exchange.transporter,
  );
}
